import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { CapManageSidebar } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageSidebar/CapManageSidebar';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { createContext, useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';
import { useCapManageController } from './useCapManageController';
import { DealDropdown } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealDropdown/DealDropdown';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils.tsx';
import { DashboardGrid } from '@dealstack/frontend/shared/layout/DashboardGrid/DashboardGrid.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { DealDirectoryContent } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/DealDirectoryContent.tsx';
import { Button, Flex, Menu, Text } from '@mantine/core';
import { CapInvalidTransactionsWarning } from '@dealstack/frontend/shared/components/CapInvalidTransactionsWarning/CapInvalidTransactionsWarning.tsx';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	OneSchemaLegalPersonRowValueDto,
	OneSchemaNaturalPersonRowValueDto,
	OneSchemaTemplateKey,
	PersonType,
} from '@dealstack/shared/lib/generated';
import { StyledCapMenuItem } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu.tsx';
import { OneSchemaImporterModal } from '@dealstack/frontend/shared/components/OneSchema/OneSchemaImporterModal.tsx';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	bulkUploadLegalPersons,
	bulkUploadNaturalPersons,
	createOneSchemaPersonsSession,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { ONE_SCHEMA_WARNING_ERROR_CODE } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/useTransactionsController.tsx';
import { formatOneSchemaDateToIsoString } from '@dealstack/shared/lib/format/dates/date.ts';
import { TransactionFromDocumentModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionFromDocumentModal/TransactionFromDocumentModal.tsx';
import type { RichCanonicalEntity } from '@dealstack/shared/lib/temporal/documentExtraction/types';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { mapOneSchemaRowToEmployment } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.utils.ts';

const MAX_CANONICAL_HINTS = 500;

const buildCanonicalHints = (
	names: ReadonlyArray<string | null | undefined>
): RichCanonicalEntity[] => {
	const seen = new Set<string>();
	const out: RichCanonicalEntity[] = [];
	for (const raw of names) {
		const canonical = raw?.trim();
		if (!canonical || seen.has(canonical)) {
			continue;
		}
		seen.add(canonical);
		out.push({ canonical });
		if (out.length >= MAX_CANONICAL_HINTS) {
			break;
		}
	}
	return out;
};

export const CapManageControllerContext = createContext<ReturnType<typeof useCapManageController>>(
	{} as ReturnType<typeof useCapManageController>
);

export const CapManageEntityFlow = () => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { deals, isAllTransactionValidInDeal, navigateToEntityWithInvalidTransactions } =
		useContext(DealContext) || {};
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const capManageFlowController = useCapManageController();
	const {
		dealId = '',
		isInitialLoading,
		dealName,
		isForceDeleteFlagLoading,
		createEntityMode,
		setCreateEntityMode,
		setSelectedEntityId,
		selectedEntityId,
		isFetchingPersons,
		selectedEntityName,
		refetchPersons,
		refreshCapDetails,
		personsSelectOptions,
	} = capManageFlowController;

	const transactionFromDocumentEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_TRANSACTION_FROM_DOCUMENT
	).value;
	const [showTransactionFromDocumentModal, setShowTransactionFromDocumentModal] = useState(false);
	const knownPersons = useMemo<RichCanonicalEntity[]>(
		() => buildCanonicalHints((personsSelectOptions ?? []).map((p) => p.label)),
		[personsSelectOptions]
	);

	// Existing securities on the selected entity, passed to the extraction pipeline
	// as canonical hints so the AI can recognise/canonicalise security names it
	// finds in the document (mirrors the persons hints above).
	const securitiesEndpoint = formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
		personId: selectedEntityId,
	});
	const getSecuritiesRequest = useQuery({
		queryKey: [securitiesEndpoint],
		queryFn: ({ signal }) => getPersonSecurities(securitiesEndpoint, undefined, signal),
		enabled: !!selectedEntityId && transactionFromDocumentEnabled,
	});
	const knownSecurities = useMemo<RichCanonicalEntity[]>(
		() => buildCanonicalHints((getSecuritiesRequest.data?.securities ?? []).map((s) => s.name)),
		[getSecuritiesRequest.data?.securities]
	);

	const breadcrumbs = useMemo(() => {
		if (inPageFeatureFlag) {
			return [
				{
					name: dealName ?? 'Deal',
					url: generatePath(ROUTES.DEAL, { dealId }),
					element: (
						<DealDropdown
							key="deal-dropdown"
							currentDealId={dealId}
							deals={deals ?? []}
						/>
					),
				},
			];
		} else {
			return [
				...makeDealPageBreadcrumbs({
					tCommonUi,
					dealId,
					dealName,
					deals: deals ?? [],
				}),
				{
					name: tCapManage('header.title_create'),
					url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}`, {
						dealId,
						personId: '',
					}),
				},
			];
		}
	}, [dealName, dealId, deals, inPageFeatureFlag, tCommonUi, tCapManage]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	const openCreateMode = () => {
		setCreateEntityMode(PersonType.LEGAL);
		setSelectedEntityId(null);
	};

	const oneSchemaUserConfig = useAppSelector((state) => state.user.data?.oneSchemaConfig);

	const [oneSchemaLoading, setOneSchemaLoading] = useState(false);
	const [oneSchemaTemplateKey, setOneSchemaTemplateKey] = useState<OneSchemaTemplateKey | null>(
		null
	);

	const bulkUploadLegalPersonsRequest = useMutation({
		mutationFn: bulkUploadLegalPersons,
	});
	const bulkUploadNaturalPersonsRequest = useMutation({
		mutationFn: bulkUploadNaturalPersons,
	});

	const onBulkUploadPersons = ({
		records,
		error_records,
	}: {
		records: OneSchemaLegalPersonRowValueDto[] | OneSchemaNaturalPersonRowValueDto[];
		error_records?: {
			data: OneSchemaLegalPersonRowValueDto[] | OneSchemaNaturalPersonRowValueDto[];
			errors: {
				[key: string]: number[];
			};
		}[];
	}) => {
		const filteredWarningRecords = (error_records || [])
			.filter((entry) => {
				const isErrorsInRow = Object.values(entry.errors)
					.flat()
					.some((code) => code !== ONE_SCHEMA_WARNING_ERROR_CODE);

				return !isErrorsInRow;
			})
			.map((entry) => entry.data);

		if (oneSchemaTemplateKey === OneSchemaTemplateKey.UPLOAD_LEGAL_PERSONS) {
			const allPersons = [
				...records,
				...filteredWarningRecords,
			] as OneSchemaLegalPersonRowValueDto[];

			bulkUploadLegalPersonsRequest.mutate(
				{
					data: {
						persons: allPersons.map((p) => ({
							name: p.name,
							corporateRegistrationNumber: p.reg_number,
						})),
					},
					dealId,
				},
				{
					onSuccess: () => {
						showToastV2();
						void refetchPersons();
						void refreshCapDetails();

						setOneSchemaTemplateKey(null);
					},
				}
			);
		} else {
			const allPersons = [
				...records,
				...filteredWarningRecords,
			] as OneSchemaNaturalPersonRowValueDto[];

			bulkUploadNaturalPersonsRequest.mutate(
				{
					data: {
						persons: allPersons.map((p) => ({
							firstName: p.first_name,
							lastName: p.last_name,
							email: p.email_address,
							phoneNumber: p.phone_number,
							dateOfBirth: p.date_of_birth
								? formatOneSchemaDateToIsoString(p.date_of_birth)
								: undefined,
							placeOfBirth: p.place_of_birth,
							countryOfBirth: p.country_of_birth,
							nationality: p.nationality,
							taxResidence: p.tax_residence
								? {
										countryCode: p.tax_residence,
										number: p.tax_number ?? null,
										authority: null,
									}
								: undefined,
							employment: mapOneSchemaRowToEmployment(p),
						})),
					},
					dealId,
				},
				{
					onSuccess: () => {
						showToastV2();
						void refetchPersons();
						void refreshCapDetails();

						setOneSchemaTemplateKey(null);
					},
					onError: (error) => {
						showToastV2({
							state: 'error',
							message: getUserFacingErrorMessage(error),
						});
					},
				}
			);
		}
	};

	const createOneSchemaSessionRequest = useMutation({
		mutationFn: createOneSchemaPersonsSession,
	});

	const openOneSchemaModal = (templateKey: OneSchemaTemplateKey) => {
		setOneSchemaLoading(true);

		createOneSchemaSessionRequest.mutate(
			{
				dealId,
				oneSchemaJwt: oneSchemaUserConfig?.oneSchemaJwt ?? '',
				oneSchemaClientId: oneSchemaUserConfig?.oneSchemaClientId ?? '',
				oneSchemaTemplateKey: templateKey,
			},
			{
				onSuccess: (response) => {
					if (!response || response.error) {
						showToastV2({
							state: 'error',
							title: response ? response.error : '',
							message: response?.message || 'Failed to create one schema session',
						});
						setOneSchemaLoading(false);
						return;
					}

					setOneSchemaTemplateKey(templateKey);
				},
				onError: (error) => {
					const message = getUserFacingErrorMessage(error);
					if (message) {
						showToastV2({
							state: 'error',
							message,
						});
					}
					setOneSchemaLoading(false);
				},
			}
		);
	};

	return (
		<StyedCapManageEntityFlowContainer>
			<BreadcrumbsProvider>
				<CapManageControllerContext.Provider value={capManageFlowController}>
					{isInitialLoading && isForceDeleteFlagLoading && <InlineLoader />}

					<DashboardGrid
						pageTitle={selectedEntityName || tCapManage('header.title_manage')}
						headerActions={
							<Flex gap="0.5rem" align="center">
								<PermissionWrapper
									resourceType={ResourceType.DEAL}
									resourceId={defaultToEmptyString(dealId)}
									permissionRole={PermissionRole.EDITOR}
								>
									{transactionFromDocumentEnabled && (
										<Button
											variant="default"
											size="md"
											leftSection={<Icon variant="document-v3" size="lg" />}
											onClick={() =>
												setShowTransactionFromDocumentModal(true)
											}
										>
											{tCapManage(
												'transactions.header_actions.add_transaction_from_document'
											)}
										</Button>
									)}
									<Menu shadow="md" position="bottom-end" withinPortal>
										<Menu.Target>
											<Button
												variant="default"
												size="md"
												leftSection={
													<Icon variant="upload-v3-thin" size="lg" />
												}
												loading={oneSchemaLoading}
											>
												{tCapManage('sidebar.create_entity_menu.upload')}
											</Button>
										</Menu.Target>

										<Menu.Dropdown>
											<Flex gap="0.25rem" direction="column">
												<StyledCapMenuItem
													p="0.5rem"
													onClick={() => {
														openOneSchemaModal(
															OneSchemaTemplateKey.UPLOAD_LEGAL_PERSONS
														);
													}}
												>
													<Text size="B6Medium">
														{tCapManage(
															'sidebar.create_entity_menu.upload_legal'
														)}
													</Text>
												</StyledCapMenuItem>

												<StyledCapMenuItem
													p="0.5rem"
													onClick={() => {
														openOneSchemaModal(
															OneSchemaTemplateKey.UPLOAD_NATURAL_PERSONS
														);
													}}
												>
													<Text size="B6Medium">
														{tCapManage(
															'sidebar.create_entity_menu.upload_natural'
														)}
													</Text>
												</StyledCapMenuItem>
											</Flex>
										</Menu.Dropdown>
									</Menu>

									<Button
										disabled={!!createEntityMode}
										data-testid={
											TEST_ELEMENT_IDS.CAP_DEAL_DIRECTORY_CREATE_ENTITY
										}
										variant="default"
										size="md"
										leftSection={<Icon variant="add" size="lg" />}
										onClick={openCreateMode}
									>
										{tCapManage('sidebar.create_entity_menu.create_new')}
									</Button>
								</PermissionWrapper>
								{!isAllTransactionValidInDeal && (
									<CapInvalidTransactionsWarning
										showInvalidTransactions={
											navigateToEntityWithInvalidTransactions
										}
									/>
								)}
							</Flex>
						}
						leftSidebarLoading={isFetchingPersons}
						sidebarContent={<CapManageSidebar />}
						mainContent={<DealDirectoryContent />}
					/>
				</CapManageControllerContext.Provider>
			</BreadcrumbsProvider>

			{transactionFromDocumentEnabled && (
				<TransactionFromDocumentModal
					opened={showTransactionFromDocumentModal}
					onClose={() => setShowTransactionFromDocumentModal(false)}
					knownPersons={knownPersons}
					knownSecurities={knownSecurities}
				/>
			)}

			{!!oneSchemaUserConfig?.oneSchemaClientId && !!oneSchemaTemplateKey && (
				<OneSchemaImporterModal
					isOpen={!!oneSchemaTemplateKey}
					onRequestClose={() => setOneSchemaTemplateKey(null)}
					onSuccess={(response) => onBulkUploadPersons(response)}
					onLaunched={() => {
						setOneSchemaLoading(false);
					}}
					clientId={oneSchemaUserConfig?.oneSchemaClientId}
					userJwt={oneSchemaUserConfig?.oneSchemaJwt}
					templateKey={oneSchemaTemplateKey}
					devMode={process.env.NODE_ENV !== 'production'}
					importConfig={{ type: 'local' }}
				/>
			)}
		</StyedCapManageEntityFlowContainer>
	);
};

const StyedCapManageEntityFlowContainer = styled(PageContainer)`
	overflow: hidden;
	position: relative;
`;
